import { isAxiosError } from 'axios';

interface ApiErrorBody {
	/** FluentValidation failures, keyed by property name (GlobalExceptionHandlerMiddleware). */
	errors?: Record<string, string[]>;
	/** RFC 7807 detail, used for argument/not-found errors. */
	detail?: string;
	/** Result<T>.Error, returned by controllers as `{ error }`. */
	error?: string;
	title?: string;
}

/**
 * Turns an API failure into a sentence the user can act on.
 *
 * The API answers a failed write in three different shapes depending on where it failed —
 * a validation dictionary, an RFC 7807 `detail`, or the handler's `{ error }` — so pull the
 * most specific one available rather than showing "Request failed with status code 400".
 */
export function getApiErrorMessage(err: unknown, fallback: string): string {
	if (!isAxiosError(err)) return fallback;

	if (err.response == null) {
		return 'Could not reach the server. Check your connection and try again.';
	}

	const body = err.response.data as ApiErrorBody | undefined;

	const validationMessages = Object.values(body?.errors ?? {}).flat();
	if (validationMessages.length > 0) return validationMessages.join(' ');

	return body?.error ?? body?.detail ?? body?.title ?? fallback;
}
